/**
 * IndexedDB service for local project storage
 */

const DB_NAME = 'LoopAiMovieXDB';
const PROJECT_STORE = 'projects';
const DB_VERSION = 2;

let dbPromise = null;

const openDB = () => {
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('IndexedDB error:', request.error);
            reject(request.error);
        };

        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(PROJECT_STORE)) {
                db.createObjectStore(PROJECT_STORE, { keyPath: 'id' });
            }
        };
    });
    return dbPromise;
};

export const saveProjectToIndexedDB = async (project) => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(PROJECT_STORE, 'readwrite');
            const store = transaction.objectStore(PROJECT_STORE);
            const request = store.put(project);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.error("Failed to save to IndexedDB:", e);
        throw e;
    }
};

export const loadProjectFromIndexedDB = async (id) => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(PROJECT_STORE, 'readonly');
            const store = transaction.objectStore(PROJECT_STORE);
            const request = store.get(id);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.error("Failed to load from IndexedDB:", e);
        return null;
    }
};

export const deleteProjectFromIndexedDB = async (id) => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(PROJECT_STORE, 'readwrite');
            const store = transaction.objectStore(PROJECT_STORE);
            const request = store.delete(id);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.error("Failed to delete from IndexedDB:", e);
        throw e;
    }
};

/**
 * Get creditsUsed for all projects from IndexedDB (lightweight scan)
 * Returns a Map of projectId -> creditsUsed
 */
export const getAllProjectCredits = async () => {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const transaction = db.transaction(PROJECT_STORE, 'readonly');
            const store = transaction.objectStore(PROJECT_STORE);
            const request = store.getAll();
            request.onsuccess = () => {
                const map = new Map();
                (request.result || []).forEach(p => {
                    if (p.id && p.creditsUsed) map.set(p.id, p.creditsUsed);
                });
                resolve(map);
            };
            request.onerror = () => resolve(new Map());
        });
    } catch {
        return new Map();
    }
};

/**
 * Return every project currently cached in IDB.
 * Used by the IDB → backend bulk-sync on app boot.
 */
export const getAllLocalProjects = async () => {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const transaction = db.transaction(PROJECT_STORE, 'readonly');
            const store = transaction.objectStore(PROJECT_STORE);
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => resolve([]);
        });
    } catch {
        return [];
    }
};

/**
 * Mark a project as successfully pushed to the canonical backend so we
 * don't re-upload it on the next boot.
 *
 * If `backendProjectId` is provided, also writes it to the IDB row as
 * `projectId`. This is CRITICAL for sync idempotency: without it, a
 * locally-created project that the backend assigns a fresh UUID to on
 * first /save would re-sync on every boot (its IDB row has no projectId
 * to match against `remoteIds`, so it never matches and we POST again,
 * creating a new duplicate backend row every time).
 *
 * Tolerates missing-record by becoming a no-op.
 */
export const markProjectSynced = async (localId, backendProjectId = null) => {
    if (!localId) return;
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const transaction = db.transaction(PROJECT_STORE, 'readwrite');
            const store = transaction.objectStore(PROJECT_STORE);
            const getReq = store.get(localId);
            getReq.onsuccess = () => {
                const existing = getReq.result;
                if (!existing) { resolve(); return; }
                existing.syncedToBackend = true;
                existing.syncedAt = Date.now();
                if (backendProjectId) {
                    existing.projectId = backendProjectId;
                }
                const putReq = store.put(existing);
                putReq.onsuccess = () => resolve();
                putReq.onerror = () => resolve();
            };
            getReq.onerror = () => resolve();
        });
    } catch (e) {
        console.warn('markProjectSynced failed:', e?.message);
    }
};

